import React, { createContext, useReducer, useContext, useEffect, useCallback } from 'react';
import { mockTasks } from '../data/mockData';

// Initial state for the task context
const initialState = {
  tasks: [],
  filteredTasks: [],
  filters: {
    status: 'all',
    zone: 'all',
    priority: 'all',
    dueDate: 'all',
    searchTerm: ''
  },
  loading: false,
  error: null,
  notification: null
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_TASKS: 'SET_TASKS',
  SET_FILTERED_TASKS: 'SET_FILTERED_TASKS',
  SET_FILTERS: 'SET_FILTERS',
  ADD_TASK: 'ADD_TASK',
  UPDATE_TASK: 'UPDATE_TASK',
  DELETE_TASK: 'DELETE_TASK',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION'
};

// Reducer function
const taskReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case ACTION_TYPES.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case ACTION_TYPES.SET_TASKS:
      return { ...state, tasks: action.payload, loading: false };
    
    case ACTION_TYPES.SET_FILTERED_TASKS:
      return { ...state, filteredTasks: action.payload };
    
    case ACTION_TYPES.SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } };
    
    case ACTION_TYPES.ADD_TASK:
      return { ...state, tasks: [action.payload, ...state.tasks], loading: false };
    
    case ACTION_TYPES.UPDATE_TASK:
      return {
        ...state,
        tasks: state.tasks.map(task => 
          task.id === action.payload.id ? { ...task, ...action.payload.updates } : task
        ),
        loading: false
      };
    
    case ACTION_TYPES.DELETE_TASK:
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload),
        loading: false
      };
    
    case ACTION_TYPES.SET_NOTIFICATION:
      return {
        ...state,
        notification: {
          type: action.payload.type,
          message: action.payload.message
        }
      };
    
    case ACTION_TYPES.CLEAR_NOTIFICATION:
      return { ...state, notification: null };
    
    default:
      return state;
  }
};

// Create context
export const TaskContext = createContext();

// TaskContextProvider component
export const TaskContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(taskReducer, initialState);

  // Load tasks
  const loadTasks = useCallback(async () => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    try {
      // In a real app, fetch from API
      // const response = await api.get('/tasks');
      setTimeout(() => {
        dispatch({ type: ACTION_TYPES.SET_TASKS, payload: mockTasks });
      }, 500);
    } catch (error) {
      console.error('Error loading tasks:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to load tasks. Please try again.'
      });
    }
  }, []);

  // Set filters
  const setFilters = useCallback((filters) => {
    dispatch({ type: ACTION_TYPES.SET_FILTERS, payload: filters });
  }, []);

  // Add task
  const addTask = useCallback(async (task) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    try {
      // In a real app, post to API
      // const response = await api.post('/tasks', task);
      const newTask = { ...task, id: Date.now().toString() };
      dispatch({ type: ACTION_TYPES.ADD_TASK, payload: newTask });
      return newTask;
    } catch (error) {
      console.error('Error adding task:', error);
      throw error;
    }
  }, []);

  // Update task
  const updateTask = useCallback(async (taskId, updates) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    try {
      // In a real app, patch to API
      // await api.patch(`/tasks/${taskId}`, updates);
      dispatch({ 
        type: ACTION_TYPES.UPDATE_TASK, 
        payload: { id: taskId, updates } 
      });
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  }, []);

  // Delete task
  const deleteTask = useCallback(async (taskId) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    try {
      // In a real app, delete from API
      // await api.delete(`/tasks/${taskId}`);
      dispatch({ type: ACTION_TYPES.DELETE_TASK, payload: taskId });
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  }, []);

  // Complete task
  const completeTask = useCallback(async (taskId) => {
    return updateTask(taskId, { status: 'completed' });
  }, [updateTask]);

  // Show notification
  const showNotification = useCallback((type, message, duration = 3000) => {
    dispatch({ 
      type: ACTION_TYPES.SET_NOTIFICATION, 
      payload: { type, message } 
    });
    
    setTimeout(() => {
      dispatch({ type: ACTION_TYPES.CLEAR_NOTIFICATION });
    }, duration);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: null });
  }, []);

  // Apply filters when tasks or filters change
  useEffect(() => {
    if (state.tasks.length > 0) {
      const filterTasks = () => {
        const { tasks, filters } = state;
        let filtered = [...tasks];
        
        // Apply status filter
        if (filters.status !== 'all') {
          filtered = filtered.filter(task => task.status === filters.status);
        }
        
        // Apply zone filter
        if (filters.zone !== 'all') {
          filtered = filtered.filter(task => task.zoneId === filters.zone);
        }
        
        // Apply priority filter
        if (filters.priority !== 'all') {
          filtered = filtered.filter(task => task.priority === filters.priority);
        }
        
        // Apply search term filter
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filtered = filtered.filter(task => 
            task.title.toLowerCase().includes(searchLower) || 
            task.description.toLowerCase().includes(searchLower)
          );
        }
        
        // Sort by priority (high to low) and then by due date (closest first)
        filtered.sort((a, b) => {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          if (priorityOrder[a.priority] > priorityOrder[b.priority]) return -1;
          if (priorityOrder[a.priority] < priorityOrder[b.priority]) return 1;
          return new Date(a.dueDate) - new Date(b.dueDate);
        });
        
        dispatch({ type: ACTION_TYPES.SET_FILTERED_TASKS, payload: filtered });
      };
      
      filterTasks();
    }
    // We're intentionally not including state in the dependency array to prevent infinite loops
    // The effect already depends on state.tasks and state.filters which are in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tasks, state.filters, dispatch]);

  // Load tasks on mount
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Context value
  const contextValue = {
    state,
    loadTasks,
    setFilters,
    addTask,
    updateTask,
    deleteTask,
    completeTask,
    showNotification,
    clearError
  };

  return (
    <TaskContext.Provider value={contextValue}>
      {children}
    </TaskContext.Provider>
  );
};

// Custom hook for using the task context
export const useTask = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTask must be used within a TaskContextProvider');
  }
  return context;
};
